import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Spinner } from '@cloudscape-design/components';
import { UserManager, type User } from 'oidc-client-ts';
import { AuthContext, type AuthContextValue, type AuthUser } from '@deloitte-us-consulting/gp-sdk';
import { oidcConfig, cognitoSignOutUrl } from './config';
import AccessRestricted from './AccessRestricted';
import { AUTH_EXEMPT_PATHS, captureRedirectState } from './postLoginRedirect';

/**
 * Shell-owned auth. The SHELL computes the session and provides `@deloitte-us-consulting/gp-sdk`'s
 * AuthContext; federated remotes consume it via `useAuth()` and never run their
 * own OIDC flow.
 *
 * DEV STANDALONE: when no Cognito pool is configured (or VITE_DEV_STANDALONE=true),
 * the shell provides a stub admin user so `pnpm dev` renders without a real login.
 * Real login (Cognito → Entra) engages once VITE_USER_POOL_ID etc. are set — see
 * the gp-platform skill SETUP-CHECKLIST.md and the pending Entra app registration.
 */
const poolId = import.meta.env.VITE_USER_POOL_ID as string | undefined;
const STANDALONE = !poolId || import.meta.env.VITE_DEV_STANDALONE === 'true';

const manager: UserManager | null = STANDALONE ? null : new UserManager(oidcConfig);

// AUTH_EXEMPT_PATHS (routes that run their own redirect side-effect and must NOT be
// intercepted by the auth gate's default "no session -> signinRedirect()" behavior
// below, nor ever be captured/returned as a post-login destination — see
// postLoginRedirect.ts) now lives in ./postLoginRedirect, shared with AuthCallback.
// /auth/graph-callback (ADR 0019) is a popup/iframe window for the separate
// Graph OAuth flow, not the primary Cognito login — it shares this app's
// bundle/origin (so mounts AuthProvider too), but must never trigger the
// primary signinRedirect() itself.

// Email-domain allowlist (defense-in-depth on top of single-tenant Entra). Comma-separated;
// unset/blank defaults to deloitte.com. An empty parsed list means "no restriction". Only
// enforced in real (non-standalone) mode — the dev stub user (dev@localhost) is exempt.
const ALLOWED_EMAIL_DOMAINS = ((import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS as string | undefined)
  ?.trim()
  ? (import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS as string).split(',')
  : ['deloitte.com']
)
  .map((d) => d.trim().toLowerCase())
  .filter(Boolean);

function emailDomainAllowed(email: string): boolean {
  if (ALLOWED_EMAIL_DOMAINS.length === 0) return true; // no restriction configured
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  return ALLOWED_EMAIL_DOMAINS.includes(domain);
}

const DEV_USER: AuthUser = {
  sub: 'dev-user',
  email: 'dev@localhost',
  name: 'Dev User',
  platformRole: 'gp-admin',
  groups: ['gp-admin'],
};

function toAuthUser(u: User): AuthUser {
  const groups = (u.profile['cognito:groups'] as string[] | undefined) ?? [];
  return {
    sub: u.profile.sub,
    email: (u.profile.email as string) ?? '',
    name: (u.profile.name as string) ?? (u.profile.email as string) ?? '',
    platformRole: groups.find((g) => g === 'gp-admin') ?? groups[0] ?? null,
    groups,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [oidcUser, setOidcUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(!STANDALONE);

  useEffect(() => {
    if (!manager) return; // standalone
    let cancelled = false;

    (async () => {
      const existing = await manager.getUser();
      if (cancelled) return;
      if (existing && !existing.expired) {
        setOidcUser(existing);
        setIsLoading(false);
      } else if (AUTH_EXEMPT_PATHS.includes(window.location.pathname)) {
        setIsLoading(false);
      } else {
        // Capture the deep link the user actually asked for (path + search + hash)
        // as OIDC `state` so AuthCallback can send them back to it after Entra
        // hands control back, instead of always landing on the home page.
        await manager.signinRedirect({ state: captureRedirectState() });
      }
    })();

    const onAdd = (u: User) => setOidcUser(u);
    const onRemove = () => setOidcUser(null);
    manager.events.addUserLoaded(onAdd);
    manager.events.addUserUnloaded(onRemove);
    manager.events.addAccessTokenExpired(onRemove);
    return () => {
      cancelled = true;
      manager.events.removeUserLoaded(onAdd);
      manager.events.removeUserUnloaded(onRemove);
      manager.events.removeAccessTokenExpired(onRemove);
    };
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    if (STANDALONE) {
      return {
        user: DEV_USER,
        accessToken: null,
        isLoading: false,
        signIn: () => {},
        signOut: () => {},
      };
    }
    return {
      user: oidcUser ? toAuthUser(oidcUser) : null,
      accessToken: oidcUser?.access_token ?? null,
      isLoading,
      signIn: () => manager?.signinRedirect({ state: captureRedirectState() }),
      signOut: () => {
        const idToken = oidcUser?.id_token;
        manager?.removeUser();
        window.location.href = cognitoSignOutUrl(idToken);
      },
    };
  }, [oidcUser, isLoading]);

  // Render-gate: while a real session is still resolving (initial check, or redirecting to
  // Entra), show a lightweight loader instead of the app so the shell never flashes before the
  // login redirect. Safe for AUTH_EXEMPT_PATHS — the effect flips isLoading to false on those
  // paths, so the callback/post-logout routes mount and run their own redirect. Standalone dev
  // is unaffected (isLoading starts false). The comprehensive fix is the #62 lightweight auth
  // bootstrap.
  if (!STANDALONE && isLoading) {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}>
        <Spinner size="large" />
      </div>
    );
  }

  // Domain guard: an authenticated identity whose email domain isn't allowlisted gets a
  // terminal screen instead of the app (no redirect loop back into login).
  if (!STANDALONE && oidcUser) {
    const email = (oidcUser.profile.email as string) ?? '';
    if (!emailDomainAllowed(email)) {
      return (
        <AccessRestricted
          email={email}
          onSignOut={() => {
            const idToken = oidcUser.id_token;
            manager?.removeUser();
            window.location.href = cognitoSignOutUrl(idToken);
          }}
        />
      );
    }
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export { manager as userManager };
